import client from './client';
import {
  TimeOffRequestSummary,
  TimeOffRequestDetail,
  TimeOffRequestFilters,
  CalendarResponse,
  CashOutRequestSummary,
} from '../interfaces/TimeOffRequest';
import { TimeOffPaidGateBlocking } from '../interfaces/TimeOff';

export const getTimeOffRequests = async (
  filters?: TimeOffRequestFilters
): Promise<TimeOffRequestSummary[]> => {
  const params: Record<string, any> = {};
  if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  if (filters?.altEmployeeID) params.altEmployeeID = filters.altEmployeeID;

  const response = await client.get('/admin/timeOff/requests', { params });
  return response.data;
};

export const getTimeOffRequestById = async (
  id: number
): Promise<TimeOffRequestDetail> => {
  const response = await client.get(`/admin/timeOff/requests/${id}`);
  return response.data;
};

export interface ApproveTimeOffOptions {
  /**
   * Unpaid Time (TAS-586): approve even though the employee still has paid time. Only send it
   * after the approver saw the 422 PAID_NOT_EXHAUSTED and confirmed — the API stamps who did it.
   */
  overrideExhausted?: boolean;
}

/**
 * Throws a `TimeOffActionError`. A 422 `PAID_NOT_EXHAUSTED` carries `blocking` — the paid
 * categories still holding time — so the caller can offer "Approve anyway".
 */
export const approveTimeOffRequest = async (
  id: number,
  notes?: string,
  options?: ApproveTimeOffOptions
): Promise<void> => {
  try {
    await client.post(`/admin/timeOff/requests/${id}/approve`, {
      notes: notes ?? null,
      ...(options?.overrideExhausted ? { overrideExhausted: true } : {}),
    });
  } catch (err) {
    throw toActionError(err, 'Failed to approve the time off request.');
  }
};

export const denyTimeOffRequest = async (
  id: number,
  notes?: string
): Promise<void> => {
  await client.post(`/admin/timeOff/requests/${id}/deny`, { notes: notes ?? null });
};

// ── Admin corrections on an already-approved request ─────────────────────────

/**
 * Structured codes from `denyApproved` / `modifyHours` (422), `approve` (422) and the on-behalf
 * create (400/409). The API sends other validation codes too (NO_POLICY, WEEKEND_DAY, …); they
 * arrive as plain strings and the message is what gets shown.
 */
export type TimeOffActionErrorCode =
  | 'DAY_OVER_EIGHT'
  | 'INSUFFICIENT_BALANCE'
  | 'ANNUAL_CAP'
  /** Unpaid Time is full 8-hour days only; modify may only drop a day (8 → 0). */
  | 'FULL_DAYS_ONLY'
  /** Unpaid Time needs paid time used up first — `blocking` lists what's left. */
  | 'PAID_NOT_EXHAUSTED'
  /** Unpaid Time can't share a day with other time off — `dates` lists the clashes. */
  | 'OVERLAPS_OTHER_TIME_OFF'
  /** Unpaid Time can't land on a day already clocked/worked — `dates` lists them. */
  | 'DAY_HAS_PUNCHES';

/** One row of the modify payload. `hours: 0` drops that day from the request. */
export interface TimeOffModifyDay {
  requestDate: string;
  hours: number;
}

/**
 * Carries the API's structured refusal through to the modal.
 *
 * The 422 body is the whole point — it names the offending day and the ceiling, which
 * is what the caller shows inline. A plain Error would flatten that to a generic message.
 */
export class TimeOffActionError extends Error {
  status?: number;
  code?: TimeOffActionErrorCode;
  requestDate?: string;
  workedHours?: number;
  maxHours?: number;
  shortfallHours?: number;
  remainingHours?: number;
  /** PAID_NOT_EXHAUSTED: the paid categories still holding time. */
  blocking?: TimeOffPaidGateBlocking[];
  /** OVERLAPS_OTHER_TIME_OFF / DAY_HAS_PUNCHES: the offending dates, when sent. */
  dates?: string[];

  constructor(message: string) {
    super(message);
    this.name = 'TimeOffActionError';
    // Subclassing Error still needs this when the bundle is downlevelled.
    Object.setPrototypeOf(this, TimeOffActionError.prototype);
  }
}

interface TimeOffActionErrorBody {
  message?: string;
  error?: string;
  code?: TimeOffActionErrorCode;
  requestDate?: string;
  workedHours?: number;
  maxHours?: number;
  shortfallHours?: number;
  remainingHours?: number;
  blocking?: TimeOffPaidGateBlocking[];
  dates?: string[];
}

function toActionError(err: unknown, fallback: string): TimeOffActionError {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response;
  const body = (response?.data ?? {}) as TimeOffActionErrorBody;
  const out = new TimeOffActionError(body.message || body.error || fallback);
  out.status = response?.status;
  out.code = body.code;
  out.requestDate = body.requestDate;
  out.workedHours = body.workedHours;
  out.maxHours = body.maxHours;
  out.shortfallHours = body.shortfallHours;
  out.remainingHours = body.remainingHours;
  out.blocking = Array.isArray(body.blocking) ? body.blocking : undefined;
  out.dates = Array.isArray(body.dates) ? body.dates : undefined;
  return out;
}

/** Denies a request that is already APPROVED, crediting its hours back. Reason is required. */
export const denyApprovedTimeOffRequest = async (
  id: number,
  reason: string
): Promise<void> => {
  try {
    await client.post(`/admin/timeOff/requests/${id}/denyApproved`, { reason });
  } catch (err) {
    throw toActionError(err, 'Failed to deny the approved request.');
  }
};

/** Rewrites the per-day hours on an APPROVED request. Dates must already be on the request. */
export const modifyTimeOffRequestHours = async (
  id: number,
  reason: string,
  days: TimeOffModifyDay[]
): Promise<{ success: boolean; totalHours: number }> => {
  try {
    const response = await client.post(`/admin/timeOff/requests/${id}/modifyHours`, {
      reason,
      days,
    });
    return response.data;
  } catch (err) {
    throw toActionError(err, 'Failed to change the hours on this request.');
  }
};

// ── Submitting a request on an employee's behalf ─────────────────────────

/** One row of GET /admin/timeOff/eligibleEmployees — the admin's scoped roster. */
export interface EligibleTimeOffEmployee {
  employeeID: number;
  altEmployeeID?: string | null;
  name: string;
  /**
   * Server-computed: could this admin approve a request for this employee? The
   * "Approve now" checkbox is shown only when true — asking for it without the
   * authority is a 403 before anything is inserted.
   */
  canApprove: boolean;
}

/**
 * Employees this admin may raise a request for. Small enough to filter client-side:
 * it is the same scoped roster the approval queue is derived from.
 */
export const getEligibleTimeOffEmployees = async (): Promise<EligibleTimeOffEmployee[]> => {
  const response = await client.get('/admin/timeOff/eligibleEmployees');
  return Array.isArray(response.data) ? response.data : [];
};

/** One requested day. Weekdays only, half-hour steps, 0.5–8 h — the API re-checks all three. */
export interface NewTimeOffRequestDay {
  requestDate: string;
  hours: number;
}

export interface NewTimeOffRequestPayload {
  employeeID: number;
  timeOffCategoryID: number;
  days: NewTimeOffRequestDay[];
  notes?: string;
  /** Approve in the same call. Only valid when the employee's row says `canApprove`. */
  approveNow?: boolean;
  /**
   * Unpaid Time (TAS-586): create it even though paid time isn't used up. Only after the admin
   * saw the 409 PAID_NOT_EXHAUSTED and confirmed — the request is stamped with who overrode.
   */
  overrideExhausted?: boolean;
}

export interface NewTimeOffRequestResult {
  timeOffRequestID: number;
  status: 'PENDING' | 'APPROVED';
  /** `approveNow` was asked for but the approval failed; the request exists, still PENDING. */
  approveError?: string;
  approveErrorCode?: string;
  blocking?: TimeOffPaidGateBlocking[];
}

/**
 * POST /admin/timeOff/requests/forEmployee — creates a request on an employee's behalf.
 *
 * Unpaid Time whose paid-time gate fails answers 409 `PAID_NOT_EXHAUSTED` with `blocking`;
 * re-send with `overrideExhausted: true` once the admin confirms.
 *
 * Validation is the mobile rule set (balance, annual cap, tenure at the requested date,
 * weekdays, no holidays, contiguous, no overlap) with past dates allowed, so a refusal is
 * a 400/403 whose `message` is the only thing that tells the admin what to change —
 * `toActionError` carries it through instead of flattening it to a generic string.
 */
export const submitTimeOffRequestForEmployee = async (
  payload: NewTimeOffRequestPayload
): Promise<NewTimeOffRequestResult> => {
  try {
    const response = await client.post('/admin/timeOff/requests/forEmployee', payload);
    return response.data;
  } catch (err) {
    throw toActionError(err, 'Failed to submit the request.');
  }
};

export const getPendingTimeOffCount = async (): Promise<number> => {
  try {
    // Use dedicated endpoint if available, otherwise count PENDING from list
    const response = await client.get('/admin/timeOff/requests/pendingCount');
    return response.data?.count ?? 0;
  } catch {
    // Fallback: fetch pending list and count
    try {
      const list = await getTimeOffRequests({ status: 'PENDING' });
      return Array.isArray(list) ? list.length : 0;
    } catch {
      return 0;
    }
  }
};

export const getCalendarRequests = async (
  startDate: string,
  endDate: string
): Promise<CalendarResponse> => {
  const response = await client.get('/admin/timeOff/calendar', {
    params: { startDate, endDate },
  });
  return response.data;
};

export const getCashOutRequests = async (
  filters?: TimeOffRequestFilters
): Promise<CashOutRequestSummary[]> => {
  const params: Record<string, any> = {};
  if (filters?.status && filters.status !== 'ALL') params.status = filters.status;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  if (filters?.altEmployeeID) params.altEmployeeID = filters.altEmployeeID;

  const response = await client.get('/admin/timeOff/cashOutRequests', { params });
  return response.data;
};

export const approveCashOutRequest = async (
  id: number,
  notes?: string
): Promise<void> => {
  await client.post(`/admin/timeOff/cashOutRequests/${id}/approve`, { notes: notes ?? null });
};

export const denyCashOutRequest = async (
  id: number,
  notes?: string
): Promise<void> => {
  await client.post(`/admin/timeOff/cashOutRequests/${id}/deny`, { notes: notes ?? null });
};
